import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import api from '../../utils/api';
import { useTenant } from '../../context/TenantContext';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const Users = () => {
  const { activeTenant, tenants } = useTenant();
  const { isGlobalAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('full_name');
  const [sortDir, setSortDir] = useState('asc');
  const [selected, setSelected] = useState({});
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'Staff',
    tenant_id: '',
  });

  useEffect(() => {
    if (!activeTenant) return;
    setInviteForm((prev) => ({ ...prev, tenant_id: prev.tenant_id || activeTenant }));
  }, [activeTenant]);

  useEffect(() => {
    if (!activeTenant) return;
    setLoading(true);
    api.get(`/users?tenant_id=${activeTenant}`)
      .then((res) => setUsers(res.data || []))
      .catch(() => toast.error('Error al cargar usuarios'))
      .finally(() => setLoading(false));
  }, [activeTenant]);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return users.filter((user) => {
      if (!normalized) return true;
      return (
        user.full_name?.toLowerCase().includes(normalized) ||
        user.email?.toLowerCase().includes(normalized)
      );
    });
  }, [users, query]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    copy.sort((a, b) => {
      const aValue = (a[sortKey] || '').toString().toLowerCase();
      const bValue = (b[sortKey] || '').toString().toLowerCase();
      if (aValue < bValue) return -1 * dir;
      if (aValue > bValue) return 1 * dir;
      return 0;
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  const allSelected = sorted.length > 0 && sorted.every((u) => selected[u.id]);
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected({});
      return;
    }
    const next = {};
    sorted.forEach((user) => {
      next[user.id] = true;
    });
    setSelected(next);
  };

  const toggleRow = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleBulkEmails = () => {
    const emails = sorted.filter((u) => selected[u.id]).map((u) => u.email).filter(Boolean);
    if (!emails.length) {
      toast.error('Selecciona al menos un usuario');
      return;
    }
    navigator.clipboard?.writeText(emails.join('\n'));
    toast.success('Emails copiados');
  };

  const handleExport = () => {
    const rows = sorted.filter((u) => selected[u.id]);
    if (!rows.length) {
      toast.error('Selecciona al menos un usuario');
      return;
    }
    const header = ['full_name', 'email'];
    const csv = [
      header.join(','),
      ...rows.map((u) => [u.full_name, u.email].map((v) => `"${v || ''}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'usuarios.csv';
    link.click();
  };

  const handleInvite = async (event) => {
    event.preventDefault();
    if (!inviteForm.tenant_id) {
      toast.error('Selecciona un tenant');
      return;
    }
    try {
      setInviteLoading(true);
      await api.post('/users/invite-new', {
        full_name: inviteForm.full_name,
        email: inviteForm.email,
        password: inviteForm.password,
        role: inviteForm.role,
        tenant_id: inviteForm.tenant_id,
      });
      toast.success('Operador creado y asignado');
      setInviteForm({
        full_name: '',
        email: '',
        password: '',
        role: 'Staff',
        tenant_id: inviteForm.tenant_id,
      });
      if (inviteForm.tenant_id === activeTenant) {
        const res = await api.get(`/users?tenant_id=${activeTenant}`);
        setUsers(res.data || []);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear operador');
    } finally {
      setInviteLoading(false);
    }
  };

  return (
    <div data-testid="users-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-4xl font-heading font-bold">Usuarios</h1>
          <p className="text-muted-foreground">Gestiona los usuarios del tenant activo</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleBulkEmails}>Copiar Emails</Button>
          <Button variant="outline" onClick={handleExport}>Exportar CSV</Button>
        </div>
      </div>
      {isGlobalAdmin() && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle>Crear operador</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleInvite} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="invite-name">Nombre</Label>
                <Input
                  id="invite-name"
                  value={inviteForm.full_name}
                  onChange={(event) => setInviteForm((prev) => ({ ...prev, full_name: event.target.value }))}
                  placeholder="Nombre y apellidos"
                  disabled={inviteLoading}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email</Label>
                <Input
                  id="invite-email"
                  type="email"
                  value={inviteForm.email}
                  onChange={(event) => setInviteForm((prev) => ({ ...prev, email: event.target.value }))}
                  placeholder="correo@empresa.com"
                  disabled={inviteLoading}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-password">Password temporal</Label>
                <Input
                  id="invite-password"
                  type="password"
                  value={inviteForm.password}
                  onChange={(event) => setInviteForm((prev) => ({ ...prev, password: event.target.value }))}
                  placeholder="********"
                  disabled={inviteLoading}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Rol</Label>
                <Select
                  value={inviteForm.role}
                  onValueChange={(value) => setInviteForm((prev) => ({ ...prev, role: value }))}
                  disabled={inviteLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona un rol" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Owner">Owner</SelectItem>
                    <SelectItem value="Staff">Staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Tenant</Label>
                <Select
                  value={inviteForm.tenant_id}
                  onValueChange={(value) => setInviteForm((prev) => ({ ...prev, tenant_id: value }))}
                  disabled={inviteLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona un tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    {tenants.map((tenant) => (
                      <SelectItem key={tenant.id} value={tenant.id}>
                        {tenant.name || tenant.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button type="submit" disabled={inviteLoading}>
                  {inviteLoading ? 'Creando...' : 'Crear operador'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
      <Card className="mb-4">
        <CardContent className="p-4">
          <Input
            placeholder="Buscar por nombre o email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="max-w-xs"
          />
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium cursor-pointer" onClick={() => toggleSort('full_name')}>
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium cursor-pointer" onClick={() => toggleSort('email')}>
                  Email
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="4" className="px-6 py-4 text-center">Cargando...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan="4" className="px-6 py-4 text-center">No hay usuarios</td></tr>
              ) : (
                sorted.map((user) => (
                  <tr key={user.id} className="border-t">
                    <td className="px-4 py-4">
                      <input type="checkbox" checked={Boolean(selected[user.id])} onChange={() => toggleRow(user.id)} />
                    </td>
                    <td className="px-6 py-4">{user.full_name}</td>
                    <td className="px-6 py-4">{user.email}</td>
                    <td className="px-6 py-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard?.writeText(user.email || '');
                          toast.success('Email copiado');
                        }}
                      >
                        Copiar Email
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};
export default Users;
